import React, { createContext, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  ADD_PROGRAM_DIALOG_INITIAL_STATE,
  getKeyProcesses,
  groupByProcessType,
  INITIAL_FILE_STATE,
  prepProgramsForAnalyzing,
  structureOverlapAnalysis,
  SUCCESS_ADD_TOAST_INITIAL_STATE,
  validateProgramsForAnalyzing,
} from './programInventoryHelpers.js';
import { ToastComponent } from '@tylertech/forge';
import { messageManager } from '../System/messageManager/messageManager.js';
import { analyzePrograms } from '../../services/programService.js';
import { useUser } from '../../contexts/userContext.js';
import { useNameConventions } from '../../contexts/nameConventionsContext.js';
import { LoaderContext } from '../../contexts/loaderContext.js';

const programInventoryInitialState = {
  showAnalyzerView: false,
  graphData: [],
  selectedRows: [],
  setShowAnalyzerView: () => { },
  setSelectedRows: () => { },
  analysisResponse: null,
  setAnalysisResponse: () => { },
  showCombinePopup: false,
  setShowCombinePopup: () => { },
  showRecommendationDialog: false,
  setShowRecommendationDialog: () => { },
  budgetHasItems: false,
  setBudgetHasItems: () => { },
  addProgramDialog: ADD_PROGRAM_DIALOG_INITIAL_STATE,
  setAddProgramDialog: () => { },
  openUploadDialog: false,
  setOpenUploadDialog: () => { },
  uploadedFile: INITIAL_FILE_STATE,
  setUploadedFile: () => { },
  fileIsIllegal: true,
  setFileIsIllegal: () => { },
  loadFile: false,
};

const ProgramInventoryContext = createContext(programInventoryInitialState);

const ProgramInventoryContextProvider = ({ children }) => {
  const [costCenters, setCostCenters] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [departmentsAndDivisions, setDepartmentsAndDivisions] = useState(null);
  const [showAnalyzerView, setShowAnalyzerView] = useState(false);
  const [analysisResponse, setAnalysisResponse] = useState(null);
  const [graphData, setGraphData] = useState([]);
  const [overlapAnalysis, setOverlapAnalysis] = useState([]);
  const [introAndConclusion, setIntroAndConclusion] = useState({
    intro: '',
    conclusion: '',
  });
  const [selectedRows, setSelectedRows] = useState([]);
  const [editedRows, setEditedRows] = useState([]);
  const [showCombinePopup, setShowCombinePopup] = useState(false);
  const [showRecommendationDialog, setShowRecommendationDialog] =
    useState(false);
  const [budgetHasItems, setBudgetHasItems] = useState(false);
  const [addProgramDialog, setAddProgramDialog] = useState(
    ADD_PROGRAM_DIALOG_INITIAL_STATE,
  );
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(INITIAL_FILE_STATE);
  const [fileIsIllegal, setFileIsIllegal] = useState(true);
  const [loadFile, setLoadFile] = useState(false);
  const [isDeleteDialogOpened, setIsDeleteDialogOpened] = useState(false);
  const [flashId, setFlashId] = useState(null);
  const [successAddToast, setSuccessAddToast] = useState(
    SUCCESS_ADD_TOAST_INITIAL_STATE,
  );
  const [successCombineToast, setSuccessCombineToast] = useState(false);
  const [duplicateDialogPrograms, setDuplicateDialogPrograms] = useState([]);
  const [isMultipleDelete, setIsMultipleDelete] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [programForDeleting, setProgramForDeleting] = useState(null);
  const [mainGridDuplicatedPrograms, setMainGridDuplicatedPrograms] = useState(
    [],
  );
  const [dbElementTracker, setDbElementTracker] = useState({});
  const [triggerUploadPrograms, setTriggerUploadPrograms] = useState(false);
  const [disableSaveButton, setDisableSaveButton] = useState(true);
  const [error, setError] = useState(null);
  const [errorCount, setErrorCount] = useState(0);
  const [newUpload, setNewUpload] = useState(false);
  const { user } = useUser();
  const { nameConventions } = useNameConventions();
  const { setLoading } = useContext(LoaderContext);

  useEffect(() => {
    setShowAnalyzerView(false);
    setAnalysisResponse([]);
    setOverlapAnalysis([]);
    setGraphData([]);
    setSelectedRows([]);
  }, [user?.activeOrganization, user?.activeBudget, user?.activeCostCenters]);

  const handleClosePopup = (newProblematicPrograms) => {
    setAddProgramDialog(ADD_PROGRAM_DIALOG_INITIAL_STATE);
    setDuplicateDialogPrograms([]);
    if (!newProblematicPrograms) return;
    ToastComponent.present({
      message: messageManager(nameConventions).PROGRAM_INVENTORY.PROBLEM_SAVING_THE_PROGRAMS,
    });
  };

  const handleAnalyzePrograms = async () => {
    setLoading(true);
    const programs = prepProgramsForAnalyzing(selectedRows);
    if (validateProgramsForAnalyzing(programs)) {
      ToastComponent.present({
        message: messageManager(nameConventions).PROGRAM_INVENTORY.INVALID_PROGRAMS_FOR_ANALYSIS,
      });
      setLoading(false);
      return;
    }
    const requestBody = {
      budget_id: user?.activeBudget?.id,
      programs: programs,
    };
    analyzePrograms(requestBody)
      .then((res) => {
        const keyProcesses = getKeyProcesses(
          res.data.summary.program_processes,
        );
        const graphData = groupByProcessType(
          res.data.summary.program_processes,
        );
        const overlapAnalysis = structureOverlapAnalysis(
          res.data.summary.summary,
        );
        const introAndConclusion = {
          intro: res.data.summary.summary.introduction,
          conclusion: res.data.summary.summary.conclusion,
        };
        setGraphData(graphData);
        setOverlapAnalysis(overlapAnalysis);
        setIntroAndConclusion(introAndConclusion);
        setAnalysisResponse(keyProcesses);
        setShowAnalyzerView(true);
      })
      .catch(() => {
        ToastComponent.present({
          message: messageManager(nameConventions).SOMETHING_WENT_WRONG,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <ProgramInventoryContext.Provider
      value={{
        showAnalyzerView,
        setShowAnalyzerView,
        graphData,
        setGraphData,
        selectedRows,
        setSelectedRows,
        analysisResponse,
        setAnalysisResponse,
        showCombinePopup,
        setShowCombinePopup,
        showRecommendationDialog,
        setShowRecommendationDialog,
        budgetHasItems,
        setBudgetHasItems,
        addProgramDialog,
        setAddProgramDialog,
        openUploadDialog,
        setOpenUploadDialog,
        uploadedFile,
        setUploadedFile,
        fileIsIllegal,
        setFileIsIllegal,
        loadFile,
        setLoadFile,
        isDeleteDialogOpened,
        setIsDeleteDialogOpened,
        flashId,
        setFlashId,
        successAddToast,
        setSuccessAddToast,
        successCombineToast,
        setSuccessCombineToast,
        duplicateDialogPrograms,
        setDuplicateDialogPrograms,
        isMultipleDelete,
        setIsMultipleDelete,
        mainGridDuplicatedPrograms,
        setMainGridDuplicatedPrograms,
        searchValue,
        setSearchValue,
        serviceTypes,
        setServiceTypes,
        costCenters,
        setCostCenters,
        departmentsAndDivisions,
        setDepartmentsAndDivisions,
        dbElementTracker,
        setDbElementTracker,
        triggerUploadPrograms,
        setTriggerUploadPrograms,
        handleClosePopup,
        editedRows,
        setEditedRows,
        disableSaveButton,
        setDisableSaveButton,
        programForDeleting,
        setProgramForDeleting,
        handleAnalyzePrograms,
        overlapAnalysis,
        setOverlapAnalysis,
        introAndConclusion,
        newUpload,
        setNewUpload,
        error,
        setError,
        errorCount,
        setErrorCount,
      }}
    >
      {children}
    </ProgramInventoryContext.Provider>
  );
};

ProgramInventoryContextProvider.propTypes = {
  children: PropTypes.node,
};

export default ProgramInventoryContextProvider;
export { ProgramInventoryContext };
